/**
 * QR Code Service
 *
 * Centralized utility for generating QR code URLs using the external QR server API.
 * Mirrors the backend QrCodeService for consistency.
 */

const BASE_URL = 'https://api.qrserver.com/v1/create-qr-code/'

export interface QrCodeOptions {
  /** Size of the QR code in pixels (default: 200) */
  size?: number
  /** Image format: png, gif, jpeg, jpg, svg, eps (default: png) */
  format?: 'png' | 'gif' | 'jpeg' | 'jpg' | 'svg' | 'eps'
  /** Margin around the QR code in pixels (default: 0) */
  margin?: number
  /** QR code color in hex without # (default: '000000') */
  color?: string
  /** Background color in hex without # (default: 'FFFFFF') */
  bgcolor?: string
}

/**
 * Generates a QR code image URL for the given data.
 *
 * @param data - The data to encode in the QR code
 * @param options - Optional configuration for the QR code
 * @returns The URL to the QR code image
 *
 * @example
 * getQrCodeUrl('https://example.com')
 * // => "https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https%3A%2F%2Fexample.com"
 *
 * @example
 * getQrCodeUrl('https://example.com', { size: 400, color: 'FF0000' })
 */
export function getQrCodeUrl(data: string, options: QrCodeOptions = {}): string {
  const {
    size = 200,
    format = 'png',
    margin = 0,
    color = '000000',
    bgcolor = 'FFFFFF',
  } = options

  const params = new URLSearchParams({
    size: `${size}x${size}`,
    data,
    format,
    margin: margin.toString(),
    color,
    bgcolor,
  })

  return `${BASE_URL}?${params.toString()}`
}

/**
 * Generates a QR code URL for an event's attendee app.
 *
 * @param eventSlug - The event slug
 * @param baseUrl - The base URL for the attendee app
 * @param options - Optional QR code configuration
 */
export function getAttendeeAppQrUrl(
  eventSlug: string,
  baseUrl: string,
  options?: QrCodeOptions
): string {
  return getQrCodeUrl(`${baseUrl}/${eventSlug}`, options)
}

/**
 * Generates a QR code URL for an event's registration form.
 *
 * @param eventSlug - The event slug
 * @param baseUrl - The base URL for registration
 * @param options - Optional QR code configuration
 */
export function getRegistrationFormQrUrl(
  eventSlug: string,
  baseUrl: string,
  options?: QrCodeOptions
): string {
  return getQrCodeUrl(`${baseUrl}/${eventSlug}/register`, options)
}

export default {
  getQrCodeUrl,
  getAttendeeAppQrUrl,
  getRegistrationFormQrUrl,
}
